import { Controller } from "@hotwired/stimulus";

// Usage: Add data-controller="form" to your <form>
// Add data-action="keyup->form#submitOnKeystroke" to your input

export default class extends Controller {
  static targets = ["input", "searchInput", "submitButton"];

  connect() {
    // Focus on search input if present (only on initial load, not on Turbo updates)
    if (this.hasSearchInputTarget && !document.documentElement.hasAttribute('data-turbo-preview')) {
      const searchInput = this.searchInputTarget;
      searchInput.focus();
      // Move cursor to the end
      const value = searchInput.value;
      searchInput.value = "";
      searchInput.value = value;
    }
  }

  submitOnKeystroke(event) {
    // Clear existing timeout
    clearTimeout(this.timeout);

    const input = event.target;
    const form = input.closest('form');

    if (!form) {
      console.warn('No form found for input', input);
      return;
    }

    // Track that user is actively typing - prevent stale restorations
    const submitId = Date.now();
    this.lastSubmitId = submitId;

    // Debounce - only submit after user stops typing for 300ms
    this.timeout = setTimeout(() => {
      // Capture the ACTUAL current value at submit time (including empty string)
      const valueToSubmit = input.value;
      const cursorPosition = input.selectionStart;
      const inputName = input.name;

      // Restore input state after Turbo updates the DOM
      const restoreInputState = () => {
        // Don't restore if user has typed again (newer submit pending)
        if (this.lastSubmitId !== submitId) {
          return;
        }

        // Find the input again (Turbo may have replaced it)
        const updatedInput = form.querySelector(`[name="${inputName}"]`);
        if (updatedInput) {
          // Check if user has continued typing - if so, don't overwrite their new input
          const currentValue = updatedInput.value;

          // Only restore if the value hasn't changed (user hasn't typed more)
          if (currentValue === valueToSubmit) {
            // Restore focus and cursor position
            updatedInput.focus();
            if (typeof updatedInput.setSelectionRange === 'function') {
              updatedInput.setSelectionRange(cursorPosition, cursorPosition);
            }
          }
          // If user has typed something different, leave their new value alone
        }
      };

      // Listen for when Turbo actually renders the new content (after DOM update)
      document.addEventListener("turbo:render", restoreInputState, { once: true });
      // Fallback for frames
      document.addEventListener("turbo:frame-load", restoreInputState, { once: true });

      // Submit form - input stays enabled throughout, user can keep typing
      if (form.requestSubmit) {
        form.requestSubmit();
      } else {
        form.submit();
      }
    }, 300); // Standard 300ms debounce
  }

  submitForm(event) {
    // Prevent default form submission
    event.preventDefault();

    // Clear any pending debounced submission
    clearTimeout(this.timeout);

    // Submit immediately
    const form = event.target.closest('form');
    if (form.requestSubmit) {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }

  handleMultiSelectChange(event) {
    // Get selected values
    const dropdown = event.target;
    const form = dropdown.closest("form");

    // Store the active element to preserve focus
    const activeElement = window.getSelection();
    // Submit the form programmatically
    form.requestSubmit();

    // Restore focus if needed
    if (activeElement) {
      setTimeout(() => {
        activeElement.focusNode.focus();
      }, 800);
    }
  }

  submitting() {
    if (!this.hasSubmitButtonTarget) return
    const btn = this.submitButtonTarget
    btn.disabled = true
    btn.innerHTML = `<svg class="animate-spin h-4 w-4 inline-block mr-1.5 -mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Saving…`
  }

  autoSubmit(event) {
    const form = event.target.closest("form");
    if (!form) return;

    // Blur the active element before submit to prevent browser from
    // scrolling to top when Turbo Stream replaces the focused element
    event.target.blur();

    form.requestSubmit ? form.requestSubmit() : form.submit();
  }

  submit(event) {
    const form = this.element.closest("form") || this.element;
    if (!form) return;

    form.requestSubmit ? form.requestSubmit() : form.submit();
  }

  // Stops a click from bubbling up to an ancestor's click handler -- e.g. an
  // inline checkbox inside a card that otherwise opens a modal on click.
  stopPropagation(event) {
    event.stopPropagation();
  }
}